import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import RoleLimitsTab from './components/RoleLimitsTab';
import IPManagementTab from './components/IPManagementTab';
import GeofencingTab from './components/GeofencingTab';
import SecurityPoliciesTab from './components/SecurityPoliciesTab';
import AdvancedSettingsTab from './components/AdvancedSettingsTab';

const SystemConfiguration = () => {
  const [activeTab, setActiveTab] = useState('role-limits');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const tabs = [
    {
      id: 'role-limits',
      label: 'Límites por Rol',
      icon: 'Users',
      description: 'Configurar límites diarios de consultas por rol de usuario'
    },
    {
      id: 'ip-management',
      label: 'Gestión de IP',
      icon: 'Shield',
      description: 'Administrar listas blancas y negras de direcciones IP'
    },
    {
      id: 'geofencing',
      label: 'Geofencing',
      icon: 'MapPin',
      description: 'Configurar restricciones geográficas y cumplimiento'
    },
    {
      id: 'security-policies',
      label: 'Políticas de Seguridad',
      icon: 'Lock',
      description: 'Gestionar MFA, sesiones y controles de seguridad'
    },
    {
      id: 'advanced-settings',
      label: 'Configuración Avanzada',
      icon: 'Settings',
      description: 'Monitoreo, integración y configuraciones técnicas'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'role-limits':
        return <RoleLimitsTab />;
      case 'ip-management':
        return <IPManagementTab />;
      case 'geofencing':
        return <GeofencingTab />;
      case 'security-policies':
        return <SecurityPoliciesTab />;
      case 'advanced-settings':
        return <AdvancedSettingsTab />;
      default:
        return <RoleLimitsTab />;
    }
  };

  const currentTab = tabs?.find(tab => tab?.id === activeTab);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-60'}`}>
        <div className="p-6">
          <Breadcrumbs />
          
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Configuración del Sistema
                </h1>
                <p className="text-muted-foreground">
                  Administre políticas globales, seguridad y configuraciones técnicas del sistema VIN Pro Platform
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-foreground">Estado del Sistema</div>
                  <div className="flex items-center space-x-2 text-sm text-green-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Operacional</span>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  size="sm"
                >
                  Exportar Configuración
                </Button>
              </div>
            </div>
          </div>

          {/* System Status Banner */}
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Server" size={20} className="text-blue-600" />
                  <span className="font-medium text-blue-900">Servidor Principal</span>
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Icon name="Database" size={20} className="text-blue-600" />
                  <span className="font-medium text-blue-900">Base de Datos</span>
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Icon name="Wifi" size={20} className="text-blue-600" />
                  <span className="font-medium text-blue-900">API Externa</span>
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                </div>
              </div>
              
              <div className="text-sm text-blue-700">
                Última verificación: {new Date()?.toLocaleString('es-MX')}
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
            
            {/* Tab Description */}
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name={currentTab?.icon} size={20} className="text-primary" />
                <div>
                  <h3 className="font-medium text-foreground">{currentTab?.label}</h3>
                  <p className="text-sm text-muted-foreground">{currentTab?.description}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-background">
            {renderTabContent()}
          </div>

          {/* Footer Information */}
          <div className="mt-12 pt-6 border-t border-border">
            <div className="grid md:grid-cols-3 gap-6 text-sm text-muted-foreground">
              <div>
                <h4 className="font-medium text-foreground mb-2">Información del Sistema</h4>
                <div className="space-y-1">
                  <div>Versión: VIN Pro Platform v2.1.0</div>
                  <div>Última actualización: 15 Oct 2024</div>
                  <div>Entorno: Producción</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-foreground mb-2">Soporte Técnico</h4>
                <div className="space-y-1">
                  <div>Email: soporte@vinpro.mx</div>
                  <div>Teléfono: +52 55 1234-5678</div>
                  <div>Horario: 24/7</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-foreground mb-2">Cumplimiento</h4>
                <div className="space-y-1">
                  <div>LFPDPPP Certificado</div>
                  <div>ISO 27001 Compliant</div>
                  <div>PROFECO Autorizado</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <QuickActionButton />
    </div>
  );
};

export default SystemConfiguration;